import { useState, useCallback, useRef } from "react";

import type { CameraPosition } from "./types";

type DraggableResult = {
  readonly position: CameraPosition;
  readonly onPointerDown: (e: React.PointerEvent) => void;
  readonly setPosition: (pos: CameraPosition) => void;
};

export const useDraggablePosition = (
  initialPosition: CameraPosition,
): DraggableResult => {
  const [position, setPosition] = useState<CameraPosition>(initialPosition);
  const dragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);

      dragOffset.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };

      const onPointerMove = (ev: PointerEvent) => {
        setPosition({
          x: ev.clientX - dragOffset.current.x,
          y: ev.clientY - dragOffset.current.y,
        });
      };

      const onPointerUp = () => {
        target.releasePointerCapture(e.pointerId);
        target.removeEventListener("pointermove", onPointerMove);
        target.removeEventListener("pointerup", onPointerUp);
      };

      target.addEventListener("pointermove", onPointerMove);
      target.addEventListener("pointerup", onPointerUp);
    },
    [position.x, position.y],
  );

  return { position, onPointerDown, setPosition };
};
